import { useState, useEffect, useRef, useCallback } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { FadeIn } from '@/components/motion';
import { Card } from '@/components/ui/card';
import { Timer, ExternalLink } from 'lucide-react';
import { LiveTimer } from '../components/live-timer';
import { WasteCountersGrid } from '../components/waste-counters-grid';
import { ScenarioCards } from '../components/scenario-cards';
import { ProductivityPulse } from '../components/productivity-pulse';
import { SOURCES } from '../data/productivity-rates';
import { useProductivityStore } from '../store/productivity-store';

export function AiProductivityPage() {
  const { isRunning, toggle, reset, getElapsed } = useProductivityStore();
  const [elapsedSeconds, setElapsedSeconds] = useState(getElapsed);
  const rafRef = useRef<number>(0);

  // Auto-start on first visit (if never started before)
  const { startedAt, bankedSeconds, start } = useProductivityStore();
  useEffect(() => {
    if (startedAt === null && bankedSeconds === 0 && !isRunning) {
      start();
    }
  }, []);

  const tick = useCallback(() => {
    setElapsedSeconds(getElapsed());
    rafRef.current = requestAnimationFrame(tick);
  }, [getElapsed]);

  useEffect(() => {
    if (isRunning) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      // Update one last time when paused
      setElapsedSeconds(getElapsed());
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isRunning, tick, getElapsed]);

  return (
    <AppLayout>
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-red-950/10">
        <div className="max-w-5xl mx-auto px-4 py-8 space-y-10">
          {/* Header */}
          <FadeIn direction="up">
            <div data-tour="ai-productivity-header" className="text-center space-y-3">
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20">
                <Timer className="w-5 h-5 text-red-400" />
                <span className="text-sm font-semibold text-red-400 uppercase tracking-wider">
                  Taxímetro de Productividad
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
                Productividad <span className="text-red-500">Perdida</span>
              </h1>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Costo de oportunidad de cada segundo sin usar IA para desarrollar, basado en
                estudios de GitHub, MIT, Harvard y Microsoft.
              </p>
            </div>
          </FadeIn>

          {/* Pulse indicator */}
          <FadeIn direction="up" delay={0.1}>
            <div data-tour="ai-productivity-pulse" className="flex justify-center">
              <ProductivityPulse isRunning={isRunning} />
            </div>
          </FadeIn>

          {/* Timer */}
          <FadeIn direction="up" delay={0.2}>
            <LiveTimer
              elapsedSeconds={elapsedSeconds}
              isRunning={isRunning}
              onToggle={toggle}
              onReset={reset}
            />
          </FadeIn>

          {/* Waste counters */}
          <FadeIn direction="up" delay={0.3}>
            <div data-tour="ai-productivity-counters">
              <WasteCountersGrid elapsedSeconds={elapsedSeconds} />
            </div>
          </FadeIn>

          {/* Methodology note */}
          <FadeIn direction="up" delay={0.35}>
            <Card className="p-4 bg-muted/30 border-muted">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <span className="font-semibold text-foreground">Metodología:</span> Costo de
                oportunidad = costo/hr del desarrollador ($75/hr promedio USA) × ganancia de
                productividad con IA (~40%, promedio conservador entre +26% MIT/Harvard y +55%
                GitHub Copilot). Las tareas y PRs extra se calculan sobre promedios de 4 tareas/día
                y 5 PRs/día, aplicando los multiplicadores de cada estudio.
              </p>
            </Card>
          </FadeIn>

          {/* Scenario cards */}
          <FadeIn direction="up" delay={0.4}>
            <div data-tour="ai-productivity-scenarios">
              <ScenarioCards />
            </div>
          </FadeIn>

          {/* Sources */}
          <FadeIn direction="up" delay={0.5}>
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-center text-muted-foreground uppercase tracking-wider">
                Fuentes
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SOURCES.map((source) => (
                  <a
                    key={source.label}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2 p-3 rounded-lg border border-border/50 hover:bg-accent/50 transition-colors group"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0 group-hover:text-foreground" />
                    <div>
                      <p className="text-sm font-medium group-hover:text-foreground">
                        {source.label}
                      </p>
                      <p className="text-xs text-muted-foreground">{source.detail}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </FadeIn>

          <div className="pb-8" />
        </div>
      </div>
    </AppLayout>
  );
}
